import { getLocalTimeZone, parseDate, today } from "@internationalized/date";
import { t } from "@travily/i18n";
import memberIllustration from "@ui/assets/illustrations/member-animated.webp";
import { Icon } from "@ui/components/Icon";
import { Wizard } from "@ui/components/Wizard";
import { WizardDateField } from "@ui/components/WizardDateField";
import type { ReactNode } from "react";

const COLOR = "green" as const;
const DEFAULT_AGE_YEARS = 25;

export interface UserCreatorDateOfBirthStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. Drives the Continue button. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Current date-of-birth value as an ISO date string (`YYYY-MM-DD`), or `""` when unset. */
  value: string;
  /** Field error for the date input, or undefined when valid. */
  error?: string;
  /** Called with the new ISO date string (or `""` when cleared) on every field change. */
  onChange: (value: string) => void;
  /** Called when the field loses focus (drives blur-time validation). */
  onBlur?: () => void;
}

export function UserCreatorDateOfBirthStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  error,
  onChange,
  onBlur,
}: UserCreatorDateOfBirthStepViewProps) {
  const dateValue = value ? parseDate(value) : null;
  const todayValue = today(getLocalTimeZone());
  const focusValue = dateValue ?? todayValue.subtract({ years: DEFAULT_AGE_YEARS });

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" className="!h-[168px] !w-[112px]" src={memberIllustration} />}
        heading={t("components.views.user.onboarding.date_of_birth_view.title")}
        helper={t("components.views.user.onboarding.date_of_birth_view.description")}
      >
        <div className="w-full max-w-[600px]">
          <WizardDateField
            color={COLOR}
            errorMessage={error}
            isInvalid={Boolean(error)}
            isLabelHidden
            label={t("components.views.user.onboarding.date_of_birth_view.label")}
            leadIcon={<Icon name="date" shadow="none" type="sticker" />}
            maxValue={todayValue}
            // Validate on blur only — NOT on change. A date field's onChange fires for every
            // intermediate complete date while typing the year (0001, 0019, 0199, 1990…), so
            // change-validation floods the server with stale-value requests and races the real
            // result. Blur fires one validation when the field is complete; re-validating on the
            // next blur clears the error once the user has corrected it.
            onBlur={onBlur}
            onChange={(next) => onChange(next ? next.toString() : "")}
            placeholderValue={focusValue}
            value={dateValue}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
